import { lazy, Suspense } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import type { RouteRecord } from 'vite-react-ssg';
import { Layout } from '@/components/layout';
import HomePage from '@/pages/HomePage';
import NotFound from '@/pages/NotFound';
import App from '@/App';

// Pages we statically prerender — imported eagerly so vite-react-ssg's SSR
// renderer sees them synchronously (lazy() routes don't pick up the Helmet
// context during SSG, so they end up with index.html-fallback meta only).
import DataFilesPage from '@/pages/DataFilesPage';
import AboutPage from '@/pages/AboutPage';
import ContactPage from '@/pages/ContactPage';
import GepositPage from '@/pages/GepositPage';
import DataInServicesPage from '@/pages/DataInServicesPage';
import PostalCodesInfoPage from '@/pages/PostalCodesInfoPage';
import TermsPage from '@/pages/TermsPage';
import FaqPage from '@/pages/FaqPage';
import NewsPage from '@/pages/NewsPage';
import PrivacyPolicyPage from '@/pages/PrivacyPolicyPage';
import CookiePolicyPage from '@/pages/CookiePolicyPage';
import ShopPage from '@/pages/ShopPage';

// Helpers ------------------------------------------------------

function lazyPage(loader: () => Promise<{ default: React.ComponentType }>) {
  return async () => {
    const m = await loader();
    return { Component: m.default };
  };
}

function PageSuspense({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<div className="min-h-[60vh]" />}>{children}</Suspense>;
}

function AdminRedirect() {
  const location = useLocation();
  const subPath = location.pathname.replace(/^\/admin/, '');
  return <Navigate to={`/sv/admin${subPath}`} replace />;
}

// Lazy admin guard / layout (kept lazy to match the previous bundle split)
const AdminGuard = lazy(() =>
  import('@/components/admin/AdminGuard').then(m => ({ default: m.AdminGuard })),
);
const AdminLayout = lazy(() =>
  import('@/components/admin/AdminLayout').then(m => ({ default: m.AdminLayout })),
);

function AdminShell() {
  return (
    <PageSuspense>
      <AdminGuard>
        <AdminLayout>
          <Outlet />
        </AdminLayout>
      </AdminGuard>
    </PageSuspense>
  );
}

// Routes -------------------------------------------------------

const routes: RouteRecord[] = [
  {
    path: '/',
    element: <App />,
    children: [
      { index: true, element: <Navigate to="/sv" replace /> },

      // Public language-prefixed routes share the public Layout
      {
        path: ':lang',
        element: (
          <Layout>
            <PageSuspense>
              <Outlet />
            </PageSuspense>
          </Layout>
        ),
        children: [
          { index: true, element: <HomePage /> },
          // Eager-loaded: prerendered pages where Helmet must run during SSG.
          { path: 'datafiler', element: <DataFilesPage /> },
          { path: 'shop', element: <ShopPage /> },
          { path: 'om-oss', element: <AboutPage /> },
          { path: 'kontakt', element: <ContactPage /> },
          { path: 'geposit', element: <GepositPage /> },
          { path: 'information/data-i-vara-tjanster', element: <DataInServicesPage /> },
          { path: 'information/postnummer', element: <PostalCodesInfoPage /> },
          { path: 'information/kopvillkor', element: <TermsPage /> },
          { path: 'information/faq', element: <FaqPage /> },
          { path: 'information/nyheter', element: <NewsPage /> },
          { path: 'integritetspolicy', element: <PrivacyPolicyPage /> },
          { path: 'cookiepolicy', element: <CookiePolicyPage /> },
          // Lazy-loaded: not prerendered, only ever shown via client-side routing.
          { path: 'shop/:productId', lazy: lazyPage(() => import('@/pages/ProductPage')) },
          { path: 'cart', lazy: lazyPage(() => import('@/pages/CartPage')) },
          { path: 'checkout', lazy: lazyPage(() => import('@/pages/CheckoutPage')) },
          { path: 'order-confirmation', lazy: lazyPage(() => import('@/pages/OrderConfirmationPage')) },
          { path: 'information/postnummer/:countySlug', lazy: lazyPage(() => import('@/pages/PostalCountyPage')) },
          { path: 'information/postnummer/:countySlug/:municipalitySlug', lazy: lazyPage(() => import('@/pages/PostalMunicipalityPage')) },
          { path: 'information/postnummer/:countySlug/:municipalitySlug/:localitySlug', lazy: lazyPage(() => import('@/pages/PostalLocalityPage')) },
          { path: 'information/nyheter/:newsId', lazy: lazyPage(() => import('@/pages/NewsArticlePage')) },
          { path: 'uppsagning', lazy: lazyPage(() => import('@/pages/CancellationPage')) },
          { path: 'uppsagning/bekraftelse', lazy: lazyPage(() => import('@/pages/CancellationConfirmationPage')) },
        ],
      },

      // Admin: language-prefixed, behind AdminGuard + AdminLayout
      {
        path: ':lang/admin',
        element: (
          <Layout>
            <PageSuspense>
              <Outlet />
            </PageSuspense>
          </Layout>
        ),
        children: [
          { index: true, element: <Navigate to="products" replace /> },
          {
            path: 'login',
            lazy: lazyPage(() => import('@/pages/AdminLoginPage')),
          },
          {
            element: <AdminShell />,
            children: [
              { path: 'products', lazy: lazyPage(() => import('@/pages/AdminProductsPage')) },
              { path: 'products/:productId', lazy: lazyPage(() => import('@/pages/AdminProductEditPage')) },
              { path: 'categories', lazy: lazyPage(() => import('@/pages/AdminCategoriesPage')) },
              { path: 'orders', lazy: lazyPage(() => import('@/pages/AdminOrdersPage')) },
              { path: 'news', lazy: lazyPage(() => import('@/pages/AdminNewsPage')) },
              { path: 'news/:newsId', lazy: lazyPage(() => import('@/pages/AdminNewsEditPage')) },
              { path: 'documents', lazy: lazyPage(() => import('@/pages/AdminDocumentsPage')) },
              { path: 'terms-documents', lazy: lazyPage(() => import('@/pages/AdminTermsDocumentsPage')) },
              { path: 'search-settings', lazy: lazyPage(() => import('@/pages/AdminSearchSettingsPage')) },
              { path: 'announcement', lazy: lazyPage(() => import('@/pages/AdminAnnouncementPage')) },
              { path: 'postal-data', lazy: lazyPage(() => import('@/pages/AdminPostalDataPage')) },
              { path: 'users', lazy: lazyPage(() => import('@/pages/AdminUsersPage')) },
              { path: 'cancellations', lazy: lazyPage(() => import('@/pages/AdminCancellationsPage')) },
              { path: 'cancellation-stats', lazy: lazyPage(() => import('@/pages/AdminCancellationStatsPage')) },
              { path: 'cancellation-settings', lazy: lazyPage(() => import('@/pages/AdminCancellationSettingsPage')) },
            ],
          },
        ],
      },

      // Redirect /admin without language prefix
      { path: 'admin/*', element: <AdminRedirect /> },

      // Legacy SEO redirects — old URLs → new language-prefixed routes
      // These now also exist as 301 redirects in the GCP load balancer (PR #5),
      // but keeping the React Router fallbacks means deep-links still work
      // for users who somehow bypass the LB.
      { path: 'information/frågor-och-svar/*', element: <Navigate to="/sv/information/faq" replace /> },
      { path: 'postnummer-och-postorter-i-sverige/*', element: <Navigate to="/sv/information/postnummer" replace /> },
      { path: 'postnummer-och-postorter-i-sverige', element: <Navigate to="/sv/information/postnummer" replace /> },
      { path: 'nyheter/*', element: <Navigate to="/sv/information/nyheter" replace /> },
      { path: 'nyheter', element: <Navigate to="/sv/information/nyheter" replace /> },
      { path: 'kontakt', element: <Navigate to="/sv/kontakt" replace /> },
      { path: 'om-oss', element: <Navigate to="/sv/om-oss" replace /> },
      { path: 'tjänster/*', element: <Navigate to="/sv/datafiler" replace /> },
      { path: 'tjänster', element: <Navigate to="/sv/datafiler" replace /> },
      { path: 'utbud/*', element: <Navigate to="/sv/datafiler" replace /> },
      { path: 'utbud', element: <Navigate to="/sv/datafiler" replace /> },
      { path: 'information/konsulttjänster', element: <Navigate to="/sv/information/data-i-vara-tjanster" replace /> },
      { path: 'information/registervård', element: <Navigate to="/sv/information/data-i-vara-tjanster" replace /> },

      { path: '*', element: <NotFound /> },
    ],
  },
];

export default routes;

export { PRERENDERED_PATHS } from './prerendered-paths';
